import { CATEGORY, COLOR, COLUMN, FORMATTING, FTSE_INDEX, JARGON, LAYOUT } from "../../modules/constants"
import { addPaddingRightToLastCell, calcStats, getCell, getCellWithTooltip, headCell, setStats } from "../../modules/table.funcs"
import { formatDate_D_MMM_YYYY, formatIntDate_D_MMM_YYYY } from "../../modules/utilities"
import { initStats2 } from "../../components/layout/InitStats"

const { border1, integer, left, right, string } = FORMATTING
const borderLeft = border1
const stats2 = {}
const width = "12%"

export const createKeyDatesHeadersData = props => {
    // console.log('createKeyDatesHeadersData props:', props)
    const { portrait, showLogos, the350club } = props

    const company = headCell({
        align: left,
        colSpan: 2,
        id: COLUMN.COMPANY,
        label: "Company",
    })

    const dateCreated = headCell({
        align: right,
        colSpan: 1,
        id: COLUMN.CREATED,
        width,
    })

    const nsmLastPubDate = headCell({
        align: right,
        borderLeft,
        colSpan: 1,
        id: COLUMN.PUB_DATE_OF_LAST_PUBLISHED_AR,
        width,
    })

    const nsmLastDocDate = headCell({
        align: right,
        colSpan: 1,
        id: COLUMN.END_OF_FY_OF_LAST_PUBLISHED_AR,
        width,
    })

    const nsmLastDiff = headCell({
        align: right,
        colSpan: 1,
        id: COLUMN.DAYS_BEFORE_PUBLICATION,
        width,
    })

    const dueDate = headCell({
        align: right,
        borderLeft,
        id: COLUMN.DUE_DATE,
        width,
    })

    const lastAgm = headCell({
        align: right,
        borderLeft,
        id: COLUMN.LAST_AGM,
        width,
    })

    const logo = headCell({
        align: left,
        id: COLUMN.LOGO,
        width: LAYOUT.LOGO_WIDTH,
    })

    const starHead = headCell({
        align: left,
        id: COLUMN.STAR,
        width: LAYOUT.STAR_WIDTH,
    })

    let headCells = portrait
        ? [starHead, company, dateCreated]
        : [starHead, company, dateCreated, nsmLastPubDate, nsmLastDocDate, nsmLastDiff, dueDate, lastAgm]

    if (showLogos)
        headCells.splice(1, 0, logo) // insert logo header

    if (the350club)
        headCells.shift() // Remove starHead

    addPaddingRightToLastCell(headCells)
    // console.log('headCells:', headCells)
    return headCells
}

export const createKeyDatesFooterData = props => {
    // console.log('createLseCompaniesFooterwData props:', props)

    const { portrait, showLogos, the350club } = props
    let column = null

    for (const property in stats2)
        calcStats(stats2[property])

    column = COLUMN.COMPANY
    const suffix = ` ${stats2[column].total === 1 ? JARGON.COMPANY : JARGON.COMPANIES}`
    const company = {
        total: getCell({ align: left, column, suffix, type: integer, value: stats2[column].total }),
        mean: getCell({ column })
    }

    column = COLUMN.CREATED
    const dateCreated = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.END_OF_FY_OF_LAST_PUBLISHED_AR
    const accountsDate = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.STAR
    const starFoot = {
        total: getCell({ column }),
        mean: getCell({ column })
    }

    column = COLUMN.LEGEND
    const legend = {
        total: getCell({ column, label: "Total:" }),
        mean: getCell({ column, label: "Average:" }),
    }

    column = COLUMN.PUB_DATE_OF_LAST_PUBLISHED_AR
    const annualReportDate = {
        total: getCell({ borderLeft, column }),
        mean: getCell({ borderLeft, column }),
    }

    column = COLUMN.DAYS_BEFORE_PUBLICATION
    const daysDiff = {
        total: getCell({ column, type: integer, value: stats2[column].total }),
        mean: getCell({ column, value: stats2[column].mean }),
    }

    column = COLUMN.DUE_DATE
    const dueDate = {
        total: getCell({ borderLeft, column }),
        mean: getCell({ borderLeft, column }),
    }

    column = COLUMN.LAST_AGM
    const lastAgm = {
        total: getCell({ borderLeft, column }),
        mean: getCell({ borderLeft, column }),
    }

    const footCells = {
        totals: portrait
            ? [starFoot.total, company.total, legend.total, dateCreated.total]
            : [starFoot.total, company.total, legend.total, dateCreated.total, annualReportDate.total, accountsDate.total, daysDiff.total, dueDate.total, lastAgm.total],
        means: portrait
            ? [starFoot.mean, company.mean, legend.mean, dateCreated.mean]
            : [starFoot.mean, company.mean, legend.mean, dateCreated.mean, annualReportDate.mean, accountsDate.mean, daysDiff.mean, dueDate.mean, lastAgm.mean]
    }

    if (showLogos) {
        const logoFoot = headCell({ id: COLUMN.LOGO, align: left })

        footCells.totals.splice(1, 0, logoFoot) // insert logo footer
        footCells.means.splice(1, 0, logoFoot) // insert logo footer
    }

    if (the350club) {
        footCells.totals.shift() // Remove starFoot
        footCells.means.shift()
    }

    addPaddingRightToLastCell(footCells.totals)
    addPaddingRightToLastCell(footCells.means)
    // console.log('footCells:', footCells)
    return footCells
}

//#region Rows
const dateDivider = 10 ** -6
const yearInt = (1000 * 60 * 60 * 24 * 365) * (10 ** -6) // TODO: Handle leap year??

export const createKeyDatesRowData = props => {
    // console.log('createKeyDatesRowData props:', props)
    const { items: companiesMetaItems, stats } = props
    let rows = []
    const todayInt = new Date().getTime() * dateDivider

    initStats2(stats2)

    companiesMetaItems.forEach(companyMeta => {
        rows.push(createKeyDatesRow({ ...props, companyMeta, todayInt }))
        ++stats.numRows
    })
    // console.log('rows:', rows)
    return rows
}

const createKeyDatesRow = ({ companyMeta, portrait, showLogos, todayInt }) => {
    // console.log('companyMeta:', companyMeta)
    const dates = companyMeta.annualReport?.dates
    const diffDaysProps = getDiffDaysProps({ dates, indexBit: companyMeta.indexBit })
    const dueDateProps = getDueDateProps({  dates, indexBit: companyMeta.indexBit, todayInt })

    const cellSpec = {
        [COLUMN.COMPANY]: {
            value: 1,
        },
        [COLUMN.NAME]: {
            align: left,
            colSpan: 2,
            type: string,
            value: companyMeta.names[0],
        },
        [COLUMN.CREATED]: {
            align: right,
            sortValue: companyMeta.creationDate ?? "!", // '!' is very low ASCII value, so it sorts to the beginning
            type: string,
            value: formatDate_D_MMM_YYYY(companyMeta.creationDate),
        },
        [COLUMN.PUB_DATE_OF_LAST_PUBLISHED_AR]: {
            align: right,
            borderLeft,
            sortValue: dates?.last?.pub ?? -1,
            type: string,
            value: formatIntDate_D_MMM_YYYY(dates?.last?.pub),
        },
        [COLUMN.END_OF_FY_OF_LAST_PUBLISHED_AR]: {
            align: right,
            sortValue: dates?.last?.doc ?? -1,
            type: string,
            value: formatIntDate_D_MMM_YYYY(dates?.last?.doc),
        },
        [COLUMN.DAYS_BEFORE_PUBLICATION]: {
            align: right,
            color: diffDaysProps.color,
            sortValue: dates?.last?.diff ?? -1,
            tooltip: diffDaysProps.tooltip,
            value: dates?.last?.diff > -1 ? dates?.last?.diff : JARGON.UNKNOWN,
        },
        [COLUMN.DUE_DATE]: {
            align: right,
            borderLeft,
            color: dueDateProps.color,
            sortValue: dates?.last?.due ?? -1,
            tooltip: dueDateProps.tooltip,
            value: formatIntDate_D_MMM_YYYY(dates?.last?.due),
        },
        [COLUMN.LAST_AGM]: {
            align: right,
            borderLeft,
            sortValue: companyMeta?.agmDates?.last ?? -1,
            value: formatIntDate_D_MMM_YYYY(companyMeta?.agmDates?.last),
        },
    }

    setStats(cellSpec[COLUMN.COMPANY].value, stats2[COLUMN.COMPANY], true)
    setStats(cellSpec[COLUMN.DAYS_BEFORE_PUBLICATION].value, stats2[COLUMN.DAYS_BEFORE_PUBLICATION], true)

    const name = getCellWithTooltip(cellSpec[COLUMN.NAME])
    const dateCreated = getCellWithTooltip(cellSpec[COLUMN.CREATED])
    const lastAccounts = getCellWithTooltip(cellSpec[COLUMN.END_OF_FY_OF_LAST_PUBLISHED_AR])
    const published = getCellWithTooltip(cellSpec[COLUMN.PUB_DATE_OF_LAST_PUBLISHED_AR])
    const diff = getCellWithTooltip(cellSpec[COLUMN.DAYS_BEFORE_PUBLICATION])
    const dueDate = getCellWithTooltip(cellSpec[COLUMN.DUE_DATE])
    const lastAgm = getCellWithTooltip(cellSpec[COLUMN.LAST_AGM])

    const logo = companyMeta.logo
        ? companyMeta.logo
        : "no logo"

    const cellWithTooltips = portrait
        ? [name, dateCreated] // portrait
        : [name, dateCreated, published, lastAccounts, diff, dueDate, lastAgm]

    addPaddingRightToLastCell(cellWithTooltips)

    return {
        _id: companyMeta.ids[0],
        category: CATEGORY.COMPANY,
        cellWithTooltips,
        iconColor: COLOR.GOOD,
        ids: companyMeta.ids,
        index: companyMeta.index,
        isFavourite: companyMeta.isFavourite,
        link: `/company/${companyMeta.ids[0]}`,
        logo: showLogos ? logo : null, // insert logo
        title: companyMeta.names[0],
    }
}

// Get the "AIM All-Share" index bit
const aimIndexBit = Number(Object.keys(FTSE_INDEX).find(key => FTSE_INDEX[key] === "AIM All-Share"))

const getDiffDaysProps = ({ dates, indexBit }) => {
    // Under the Disclosure Guidance and Transparency Rules (DTR 4), Main Market Companies must publish annual financial statements within four months of the financial year end.
    // AIM-listed must publish their annual audited accounts no later than six months after the end of the financial period.
    // TODO: 6 months if AIM company
    const months = indexBit === aimIndexBit ? 6 : 4
    const diffDays = dates?.last?.diff ?? -1

    if (dates?.last?.doc === -1 || dates?.last?.pub === -1) return {
        color: null,
        tooltip: "",
    }
    else if (diffDays < 20 || diffDays > (indexBit === aimIndexBit ? 183 : 123)) return {
        color: COLOR.WARN,
        tooltip: `publication was late, taking more than ${months} months from the end of the FY`,
    }
    else return {
        color: COLOR.GOOD,
        tooltip: `publication was before the due date (${months} months after the end of the FY)`,
    }
}

const getDueDateProps = ({ dates, indexBit, todayInt }) => {
    // NB: todayInt is today in (millisecs since 1/1/1970) * (10 ** -6)
    // expectedIntDate is one year after Last Annual Report publication date
    const expectedIntDate = dates?.last?.doc > -1 ? dates.last.pub + yearInt : -1
    const todayIsOnOrBeforeExpectedDate = todayInt <= expectedIntDate
    const todayIsOnOrBeforeDueDate = todayInt <= dates?.last?.due

    if (dates.last.due < 0) return {
        color: null,
        tooltip: "",
    }
    else if (todayIsOnOrBeforeDueDate) {
        if (todayIsOnOrBeforeExpectedDate) return {
            color: COLOR.GOOD,
            tooltip: "next Annual Report is not yet due or expected",
        }
        else return { // today is after expected date
            color: COLOR.WARN,
            tooltip: "next Annual Report is expected, based on the last report's publication date, but it is not yet overdue",
        }
    } else return {
        color: COLOR.BAD,
        tooltip: `next Annual Report is overdue, as it is now more than ${indexBit === aimIndexBit ? 6 : 4} months since the end of the last FY`,
    }
}

//#endregion Rows

